import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const BrandContext = createContext({
  organization_name: "DanceHub",
  display_name: "DanceHub",
  default_country_code: "+91",
  default_timezone: "Asia/Kolkata",
  primary_color: "#7C3AED",
  setPrimaryColor: () => {},
  refresh: () => {},
});

// Helpers — adjust hex brightness for hover state.
function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
function shadeHex(hex, percent) {
  // percent: -100..100. Negative = darker, positive = lighter.
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const adj = (c) => {
    const v = parseInt(c, 16);
    const out = clamp(Math.round(v + (percent / 100) * (percent < 0 ? v : 255 - v)), 0, 255);
    return out.toString(16).padStart(2, "0");
  };
  return `#${adj(m[1])}${adj(m[2])}${adj(m[3])}`;
}

export const applyBrandColor = (color) => {
  if (!color) return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", color);
  root.style.setProperty("--brand-primary-hover", shadeHex(color, -10));
  root.style.setProperty("--brand-primary-end", shadeHex(color, -25));
};

export const BrandProvider = ({ children }) => {
  const [org, setOrg] = useState({
    organization_name: "DanceHub",
    display_name: "DanceHub",
    default_country_code: "+91",
    default_timezone: "Asia/Kolkata",
  });
  const [primaryColor, setPrimaryColorState] = useState(
    JSON.parse(localStorage.getItem("active_studio") || "null")?.branding?.primary_color || "#7C3AED"
  );

  const setPrimaryColor = useCallback((c) => {
    setPrimaryColorState(c || "#7C3AED");
  }, []);

  // Apply CSS variable whenever color changes
  useEffect(() => {
    applyBrandColor(primaryColor);
  }, [primaryColor]);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/organization");
      if (res.data?.organization) {
        setOrg({
          organization_name: res.data.organization.organization_name || "DanceHub",
          display_name: res.data.organization.display_name || "DanceHub",
          default_country_code: res.data.organization.default_country_code || "+91",
          default_timezone: res.data.organization.default_timezone || "Asia/Kolkata",
        });
        document.title = res.data.organization.display_name || "DanceHub";
      }
    } catch (e) { /* keep defaults */ }

    // Refresh branding color from currently selected studio
    try {
      const sid = localStorage.getItem("active_studio_id");
      if (sid) {
        const sRes = await api.get(`/studios/${sid}`);
        const color = sRes.data?.studio?.branding?.primary_color;
        if (color) {
          setPrimaryColorState(color);
          // Also keep localStorage in sync so reload picks the right colour pre-fetch
          const cached = JSON.parse(localStorage.getItem("active_studio") || "{}");
          cached.branding = { ...(cached.branding || {}), primary_color: color };
          localStorage.setItem("active_studio", JSON.stringify(cached));
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BrandContext.Provider value={{ ...org, primary_color: primaryColor, setPrimaryColor, refresh }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
